// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useSwiper } from "@flip/App/Components/XngCarousel/XngCarousel.context";
import {
    ArrowPosition,
    ArrowType,
    NavigationProps,
    SwiperInstance,
} from "@flip/App/Components/XngCarousel/XngCarousel.models";
import PaginationLeft from "@flip/XDL/Icons/PaginationLeft";
import PaginationRight from "@flip/XDL/Icons/PaginationRight";
import { color, xScreen } from "@flip/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import { getProp } from "@insite/mobius/utilities";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition) => ({
    size: 20,
    css: css`
        position: static;
        ${`margin-${arrowPosition === "left" ? "right" : "left"}: 10px`};
        background: transparent;
        height: 15px;
        width: 15px;
        & > ${IconWrapper} svg {
            color: ${color("common.ui4")};
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            &:hover {
                color: ${color("common.ui13")};
            }
        }
        transform: none;
        z-index: 10;
        padding: 0;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 0;
        outline: none !important;

        &:focus {
            box-shadow: none;
        }

        ${(arrowPosition === "top" || arrowPosition === "bottom") &&
        css`
            position: relative;
            inset: 0;
            transform: none;
            margin: auto;
        `};

        &.swiper-button-disabled {
            & > ${IconWrapper} {
                color: ${color("common.ui4", 0.3)};
            }
        }
    `,
});

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 15,
    },
    bottom: {
        src: ArrowDown,
        size: 15,
    },
    left: {
        src: PaginationLeft,
        size: 15,
    },
    right: {
        src: PaginationRight,
        size: 15,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

const getArrowElement = ({
    swiper,
    showOnMobile,
    arrowType,
}: {
    swiper: SwiperInstance | null;
    arrowType: string;
    showOnMobile?: boolean;
}) => {
    const arrowElement = arrowType === "next" ? swiper?.navigation.nextEl : swiper?.navigation.prevEl;

    if (!swiper || !arrowElement) {
        return {
            className: "",
            element: undefined,
        };
    }

    const navigationSwiperClassName = arrowElement.className.replace(`swiper-button-${arrowType}`, "").trim();

    const classNames = [
        `arrow-button xng-carousel-${arrowType}`,
        `${showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop"}`,
        navigationSwiperClassName,
        arrowElement.className.includes("swiper-button-lock") ? "swiper-button-lock" : "",
    ];

    return {
        className: classNames.join(" "),
        disabled: arrowElement.className.indexOf("swiper-button-disabled") > -1,
        element: arrowElement,
    };
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, loop, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];
        const [wrapperSize, setWrapperSize] = useState<{ height?: number; width?: number }>({});
        const [resizeObserveInitiated, setResizeObserveInitiated] = useState(false);

        const {
            className,
            element: arrowElement,
            disabled,
        } = getArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        useEffect(() => {
            if (!swiperInstance?.wrapperEl || resizeObserveInitiated) {
                return;
            }

            // To update when a wrapper height change
            setResizeObserveInitiated(true);
            const observer = new (window as any).ResizeObserver(([entry]: any) => setWrapperSize(entry.contentRect));
            observer.observe(swiperInstance.wrapperEl);
            return () => observer.disconnect();
        }, [swiperInstance]);

        const onClickHandler = useCallback(
            (event: any) => {
                // XNG-Changes: FLIP-933 this customization done to apply loop logic manually
                // since swiper has issue with loop when using multiple slides
                if (swiperInstance && loop) {
                    if (arrowType === "prev" && swiperInstance.isBeginning) {
                        setTimeout(() => {
                            swiperInstance.slideTo(swiperInstance.slides.length);
                        }, 0);
                    }
                    if (arrowType === "next" && swiperInstance.isEnd) {
                        setTimeout(() => {
                            swiperInstance.slideTo(0);
                        }, 0);
                    }
                }

                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [swiperInstance],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            wrapperHeight: wrapperSize.height,
            arrowPosition,
            showOnMobile,
            className: `${className} ${otherProps.className || ""}`.trim(),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
